/**
 * Remote data related to unauthenticated pages (set password, login, etc.)
 */

import {
  useMutationWithRemoteDataError,
  useQueryWithRemoteDataError,
} from '../api/queries';
import {
  fetchChatbotAccessRightRequest,
  fetchCirclesInviteTokenInfo,
  fetchMoiJwtCookie,
  fetchSetPasswordTokenData,
  fetchSupportAccessRightRequest,
  postResendSetPasswordEmail,
  postResetPassword,
  postSetPassword,
  postVerifyEmail,
  putChatbotAccessRightRequest,
  putSupportAccessRightRequest,
} from '../api/queries/unauthenticated';
import { logException } from '../common/sentry';
import { CirclesInviteTokenInfo } from '../common/types';

export function useSetPasswordData(token?: string) {
  return useQueryWithRemoteDataError(
    ['setPassword', 'token', token],
    () => fetchSetPasswordTokenData(token),
    { enabled: !!token },
  );
}

export function useSetPassword() {
  return useMutationWithRemoteDataError(postSetPassword, {
    onError: (err) => {
      logException(err);
    },
  });
}

export function useResendSetPasswordEmail() {
  return useMutationWithRemoteDataError(postResendSetPasswordEmail, {
    onError: (err) => {
      logException(err);
    },
  });
}

export function useResetPassword() {
  return useMutationWithRemoteDataError(postResetPassword, {
    onError: (err) => {
      logException(err);
    },
  });
}

export function useSupportAccessRightRequest(token?: string) {
  return useQueryWithRemoteDataError(
    ['requestSupportAccessRights', 'token', token],
    () => fetchSupportAccessRightRequest(token),
    { enabled: !!token },
  );
}

export function useModifySupportAccessRightRequest() {
  return useMutationWithRemoteDataError(putSupportAccessRightRequest, {
    onError: (err) => {
      logException(err);
    },
  });
}

export function useVerifyEmail(token?: string) {
  return useQueryWithRemoteDataError(
    ['verifyEmail', 'token', token],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => postVerifyEmail(token!),
    {
      enabled: !!token,
    },
  );
}

export function useChatbotAccessRightRequest(token?: string) {
  return useQueryWithRemoteDataError(
    ['requestChatbotAccessRights', 'token', token],
    () => fetchChatbotAccessRightRequest(token),
    { enabled: !!token },
  );
}

export function useModifyChatbotAccessRightRequest() {
  return useMutationWithRemoteDataError(putChatbotAccessRightRequest, {
    onError: (err) => {
      logException(err);
    },
  });
}

export function useCirclesInviteTokenInfo({
  token,
  campaign,
  onSuccess,
}: {
  token?: string;
  campaign?: string;
  onSuccess?: (data: CirclesInviteTokenInfo) => void;
}) {
  return useQueryWithRemoteDataError(
    ['circlesInviteTokenInfo', token, campaign],
    () => fetchCirclesInviteTokenInfo(token, campaign),
    {
      enabled: !!token,
      // Refetch only once every 24 hours.
      staleTime: 24 * 60 * 60 * 1000,
      // Keep data in cache for 15 minutes after unmounting.
      cacheTime: 15 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
      onSuccess,
    },
  );
}

export function useMoiJwtCookie({
  code,
  state,
  redirectUri,
  onSuccess,
}: {
  code: string;
  state: string;
  redirectUri: string;
  onSuccess: () => void;
}) {
  return useQueryWithRemoteDataError(
    ['moiJwtCookie', code, redirectUri],
    () => fetchMoiJwtCookie({ code, state, redirectUri }),
    {
      enabled: !!code,
      cacheTime: 0,
      retry: 2,
      refetchOnWindowFocus: false,
      onSuccess,
    },
  );
}
